import type {
  BackendController,
  Controller,
  ControllerEditable,
  ControllerService,
  Count,
  Diagnostic,
  GetControllersParams,
  Mqtt,
  Pagination,
  ServiceCatalogItem,
  TimeseriesPoint,
  TimeseriesRequestPayload,
  TotalCount,
  Tunnel,
  UpdatedController,
} from '@/types';
import request from '@/utils/request';

export const getControllers = async ({
  page,
  ordering,
  groupIds,
  pageSize = 0,
  latitudeMax,
  latitudeMin,
  longitudeMax,
  longitudeMin,
  organizationId,
  description,
  firmwareVersion,
  groupName,
  serialNumber,
}: GetControllersParams) => {
  const url = '/api/v1/controllers/';

  return (
    await request.get(url, {
      params: {
        page,
        ordering,
        group_ids: groupIds,
        page_size: pageSize,
        latitude_max: latitudeMax,
        latitude_min: latitudeMin,
        longitude_max: longitudeMax,
        longitude_min: longitudeMin,
        organization_id: organizationId,
        firmware_version: firmwareVersion,
        serial_number: serialNumber,
        group_name: groupName,
        description,
      },
    })
  ).data as Pagination<BackendController>;
};

export const getControllersCount = async (params?: GetControllersParams) => {
  const url = '/api/v1/controllers-count/';
  const cfg = params
    ? {
      params: {
        page: params.page,
        group_ids: params.groupIds,
        page_size: params.pageSize || 0,
        latitude_max: params.latitudeMax,
        latitude_min: params.latitudeMin,
        longitude_max: params.longitudeMax,
        longitude_min: params.longitudeMin,
        organization_id: params.organizationId,
        firmware_version: params.firmwareVersion,
        serial_number: params.serialNumber,
        group_name: params.groupName,
        description: params.description,
      },
    }
    : undefined;

  return (await request.get<Count>(url, cfg)).data;
};

export const getControllersTotalCount = async () => {
  const url = '/api/v1/controllers-total-count/';

  return (await request.get<TotalCount>(url)).data;
};

export const GET_CONTROLLER_REGEX =
  /\/api\/v1\/controllers\/(?<serialNumber>[A-z0-9]*)\/$/;

export const getController = async (
  serialNumber: string,
): Promise<BackendController> => {
  const url = `/api/v1/controllers/${serialNumber}/`;

  return (await request.get(url)).data as BackendController;
};

export const updateController = async ({
  serialNumber,
  data,
}: {
  serialNumber: string;
  data: ControllerEditable;
}): Promise<UpdatedController> => {
  const url = `/api/v1/controllers/${serialNumber}/`;

  return (await request.patch(url, { ...data })).data as UpdatedController;
};

export const GET_MQTT_REGEX =
  /\/api\/v1\/controllers\/(?<serialNumber>[A-z0-9]*)\/last-mqtt-data\/$/;

export const getMqtt = async (serialNumber: string): Promise<Mqtt[]> => {
  const url = `/api/v1/controllers/${serialNumber}/last-mqtt-data/`;

  return (await request.get(url)).data as Mqtt[];
};

export const getGraphData = async (
  serialNumber: string,
  data: TimeseriesRequestPayload,
): Promise<TimeseriesPoint[]> => {
  const url = `/api/v1/controllers/${serialNumber}/system-metrics/`;

  return (await request.post(url, { ...data })).data as TimeseriesPoint[];
};

export const getLastMetricsTime = async (
  serialNumber: string,
): Promise<string | null> => {
  const url = `/api/v1/controllers/${serialNumber}/last-metrics-time/`;

  return (await request.get(url)).data.lastMetricsAt;
};

export const getDiagnostic = async (
  serialNumber: string,
): Promise<Diagnostic> => {
  const url = `/api/v1/controllers/${serialNumber}/diagnostic/`;

  return (await request.get(url)).data;
};

export const requestDiagnostic = async (
  serialNumber: string,
): Promise<string> => {
  const url = `/api/v1/controllers/${serialNumber}/request-diagnostic/`;

  return (await request.post(url)).data;
};

export const downloadDiagnostic = async (dowloadId: string): Promise<File> => {
  const url = `/api/v1/download/${dowloadId}`;

  return (
    await request.get(url, {
      responseType: 'blob',
    })
  ).data;
};

export const createTunnel = async ({
  serialNumber,
  protocol,
}: {
  serialNumber: string;
  protocol: string;
}) => {
  const url = `/api/v1/controllers/${serialNumber}/tcp-tunnels/${protocol}/`;

  return (await request.post(url)).data as Tunnel;
};

export const getServiceCatalog = async (
  serialNumber: string,
): Promise<ServiceCatalogItem[]> => {
  const url = `/api/v1/controllers/${serialNumber}/service-catalog/`;

  return (await request.get(url)).data;
};

export const getServices = async (
  serialNumber: string,
): Promise<ControllerService[]> => {
  const url = `/api/v1/controllers/${serialNumber}/services/`;

  return (await request.get(url)).data;
};

export const addService = async (
  serialNumber: string,
  data: { port: number; name?: string },
): Promise<ControllerService> => {
  const url = `/api/v1/controllers/${serialNumber}/services/`;

  return (await request.post(url, data)).data;
};

export const updateService = async (
  serialNumber: string,
  port: number,
  data: { name?: string; port?: number },
): Promise<ControllerService> => {
  const url = `/api/v1/controllers/${serialNumber}/services/${port}/`;

  return (await request.patch(url, data)).data;
};

export const deleteService = async (
  serialNumber: string,
  port: number,
): Promise<void> => {
  const url = `/api/v1/controllers/${serialNumber}/services/${port}/`;

  await request.delete(url);
};

export const activateController = async ({
  token,
  organizationId,
}: {
  token: string;
  organizationId: string;
}) => {
  const url = '/api/v1/controllers-activation/activate/';

  return (await request.post(url, { token, organization: organizationId }))
    .data as Controller;
};

const VALIDATE_CONTROLLER_URL = '/api/v1/controllers-activation/validate/';
export const VALIDATE_CONTROLLER_REGEX =
  /\/api\/v1\/controllers-activation\/validate\/$/;

export const validateController = async (token: string) => {
  const url = VALIDATE_CONTROLLER_URL;

  return (await request.post(url, { token })).data as {
    controllerSerialNumber: string;
    organizationToBind: string;
  };
};

export const deleteController = async (serialNumber: string) => {
  const url = `/api/v1/controllers/${serialNumber}/`;

  await request.delete(url);
};
